import React, { useMemo, useState } from 'react';
import type { Game } from '../../types/game';
import { useUser } from '../../hooks/useUser';
import { useUtPlayerIndex } from '../../hooks/useUtPlayerIndex';
import { useGameWeather } from '../../hooks/useGameWeather';
import { Panel, SectionTitle, StatTile, StatusPill, UtEmpty, UtLoading } from '../../components/ut/primitives';
import { UtCard } from '../../components/ut/UtCard';
import { GameHero } from '../../components/ut/match/GameHero';
import { RegistrationPanel } from '../../components/ut/match/RegistrationPanel';
import { PlayersAnnounced } from '../../components/ut/match/PlayersAnnounced';
import { Lineups, type LineupRegistration } from '../../components/ut/match/Lineups';
import { LastResult } from '../../components/ut/match/LastResult';
import { UtSelectionExplainer } from '../../components/ut/match/UtSelectionExplainer';

interface RawRegistration {
  status: string;
  team?: string | null;
  using_token?: boolean;
  using_shield?: boolean;
  using_injury?: boolean;
  player?: { id: string; friendly_name: string; current_streak?: number | null } | null;
}

export interface UtGameProps {
  game: Game | null;
  isLoading: boolean;
  isRegistrationOpen: boolean;
  isRegistrationClosed: boolean;
  isUserRegistered: boolean;
  isProcessing: boolean;
  isTeamAnnouncementTime: boolean;
  onGameUpdated: () => Promise<void>;
  useToken: boolean;
  setUseToken: (v: boolean) => void;
  shieldTokenUsers: { player: { id: string; friendly_name: string }; protected_streak_value: number }[];
}

/**
 * Ultimate Team view of /games. Game.tsx keeps every query and side-effect
 * hook (registration close, team announcement, realtime) and hands the state
 * down; this only renders it.
 */
const UtGame: React.FC<UtGameProps> = ({
  game,
  isLoading,
  isRegistrationOpen,
  isUserRegistered,
  isProcessing,
  isTeamAnnouncementTime,
  onGameUpdated,
  useToken,
  setUseToken,
  shieldTokenUsers,
}) => {
  const { player } = useUser();
  const regs = useMemo(() => ((game?.game_registrations ?? []) as unknown as RawRegistration[]).filter((r) => r.player), [game]);
  const ids = useMemo(() => regs.map((r) => r.player!.id), [regs]);
  const { index, loading: indexLoading } = useUtPlayerIndex(ids);
  const weather = useGameWeather(game?.venue?.address, game?.date);
  const [explain, setExplain] = useState(false);

  if (isLoading) return <UtLoading label="Loading game" />;
  if (!game) {
    return (
      <section className="flex flex-col gap-3">
        <UtEmpty title="No upcoming game" body="The next game appears here as soon as it's scheduled." action={undefined} />
        <LastResult playerId={player?.id ?? null} />
      </section>
    );
  }

  const playerId = player?.id ?? null;
  const active = regs.filter((r) => r.status !== 'dropped_out' && r.status !== 'absent');
  const sorted = [...active].sort((a, b) => (index.get(b.player!.id)?.xp ?? 0) - (index.get(a.player!.id)?.xp ?? 0));
  const myRank = playerId ? sorted.findIndex((r) => r.player!.id === playerId) + 1 : 0;
  const tokenCount = regs.filter((r) => r.using_token).length;
  const myReg = regs.find((r) => r.player!.id === playerId);
  const teamsOut = isTeamAnnouncementTime || game.status === 'teams_announced';
  const lineupRegs: LineupRegistration[] = regs.map((r) => ({
    playerId: r.player!.id,
    name: r.player!.friendly_name,
    status: r.status,
    team: r.team === 'blue' || r.team === 'orange' ? r.team : null,
  }));

  if (teamsOut) {
    return (
      <Lineups
        gameId={game.id}
        matchNumber={game.sequence_number ?? ''}
        date={game.date}
        venueName={game.venue?.name}
        announcedAt={game.team_announcement_time}
        registrations={lineupRegs}
        index={index}
        playerId={playerId}
      />
    );
  }

  const injured = regs.filter((r) => r.using_injury);

  return (
    <section className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] items-start gap-[18px]">
      <GameHero game={game} weather={weather} onCountdownDone={onGameUpdated} />

      <div className="flex flex-col gap-3">
        {isRegistrationOpen ? (
          <RegistrationPanel
            game={game}
            playerId={playerId}
            isUserRegistered={isUserRegistered}
            myRank={myRank}
            registeredCount={active.length}
            tokenCount={tokenCount}
            myUsingToken={!!myReg?.using_token}
            isProcessing={isProcessing}
            onChanged={onGameUpdated}
            useToken={useToken}
            setUseToken={setUseToken}
          />
        ) : game.status === 'upcoming' ? (
          <Panel className="flex flex-col gap-1">
            <span className="font-ut-display text-[22px] font-extrabold uppercase">Registration not open yet</span>
            <span className="text-sm text-ut-mut">Come back when the countdown ends to register for #{game.sequence_number}.</span>
            <button type="button" onClick={() => setExplain(true)} className="mt-1 self-start text-[13px] font-semibold text-ut-acc hover:text-ut-ink">
              How selection works →
            </button>
          </Panel>
        ) : game.status === 'players_announced' ? (
          <Panel className="flex flex-col gap-3">
            <span className="font-ut-display text-[22px] font-extrabold uppercase">Players selected</span>
            <div className="grid grid-cols-3 gap-2">
              <StatTile value={active.filter((r) => r.status === 'selected').length} label="SELECTED" className="bg-ut-card2" />
              <StatTile value={active.filter((r) => r.status === 'reserve').length} label="RESERVES" className="bg-ut-card2" />
              <StatTile value={regs.filter((r) => r.status === 'dropped_out').length} label="DROPPED" className="bg-ut-card2" />
            </div>
            <span className="text-sm text-ut-mut">Teams are balanced and announced when the countdown ends.</span>
            <button type="button" onClick={() => setExplain(true)} className="self-start text-[13px] font-semibold text-ut-acc hover:text-ut-ink">
              How selection works →
            </button>
          </Panel>
        ) : (
          <Panel className="flex flex-col gap-1">
            <span className="font-ut-display text-[22px] font-extrabold uppercase">
              {isProcessing ? 'Selecting players…' : 'Registration closed'}
            </span>
            <span className="text-sm text-ut-mut">The selected players appear here shortly.</span>
          </Panel>
        )}
        <LastResult playerId={playerId} />
        <UtSelectionExplainer
          open={explain}
          onClose={() => setExplain(false)}
          matchNumber={game.sequence_number}
          maxPlayers={game.max_players}
          randomSlots={game.random_slots ?? 0}
          tokenCount={tokenCount}
        />
      </div>

      {game.status === 'players_announced' && (
        <div className="col-span-full">
          <PlayersAnnounced
            gameId={game.id}
            matchNumber={game.sequence_number}
            date={game.date}
            playerId={playerId}
            currentStreak={player?.current_streak ?? 0}
            index={index}
            onChanged={onGameUpdated}
          />
        </div>
      )}

      {game.status !== 'players_announced' && active.length > 0 && (
        <div className="col-span-full flex flex-col gap-3">
          <SectionTitle note={indexLoading ? 'Loading XP…' : 'Sorted by XP'}>Registered</SectionTitle>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-2">
            {sorted.map((r) => {
              const info = index.get(r.player!.id);
              return (
                <UtCard
                  key={r.player!.id}
                  player={{
                    id: r.player!.id,
                    name: r.player!.friendly_name,
                    xp: info?.xp ?? 0,
                    rarity: info?.rarity,
                    position: info?.position,
                    whatsappMember: info?.whatsappMember,
                    photo: info?.photo,
                  }}
                  size="mini"
                  highlight={r.player!.id === playerId}
                />
              );
            })}
          </div>
        </div>
      )}

      {(shieldTokenUsers.length > 0 || injured.length > 0) && (
        <div className="col-span-full flex flex-col gap-2">
          <SectionTitle>Protected streaks</SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            {shieldTokenUsers.map((s) => (
              <StatusPill key={s.player.id} tone="sky">
                {s.player.friendly_name} · shield · {s.protected_streak_value} protected
              </StatusPill>
            ))}
            {injured.map((r) => (
              <StatusPill key={r.player!.id} tone="gold">
                {r.player!.friendly_name} · injured
              </StatusPill>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default UtGame;
